// Keep bundle choices in their owning form. Re-entry never replaces a valid selection.
class EyeoneBundleOptions extends HTMLElement {
  #form;
  #variantInput;
  #fields;
  #selection;
  #variants;
  #activeVariant;
  #abort;
  #observer;

  connectedCallback() {
    if (this.#abort) return;
    this.#form = this.closest('form');
    this.#variantInput = this.#form?.querySelector('[name="id"]');
    this.#fields = this.querySelector('[data-eyeone-bundle-fields]');
    this.#selection = this.querySelector('[data-eyeone-bundle-selection]');
    if (!this.#form || !this.#variantInput || !this.#fields || !this.#selection) return;
    try {
      this.#variants = JSON.parse(this.querySelector('[data-eyeone-bundle-data]')?.textContent || '{}');
    } catch {
      return;
    }
    this.#activeVariant = this.dataset.currentVariant;
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    this.addEventListener('change', this.#onChange, options);
    this.#form.addEventListener('submit', this.#onSubmit, { ...options, capture: true });
    this.#form.addEventListener('formdata', this.#onFormData, options);
    this.#form.addEventListener('reset', this.#onReset, options);
    window.addEventListener('pageshow', this.#onPageShow, options);
    window.addEventListener('pagehide', this.#save, options);
    this.#observer = new MutationObserver(this.#reconcileVariant);
    this.#observer.observe(this.#variantInput, { attributes: true, attributeFilter: ['value'] });
    if (this.#variantInput.value !== this.#activeVariant) this.#reconcileVariant();
    else {
      this.#restore();
      this.#sync();
    }
  }

  disconnectedCallback() {
    this.#save();
    this.#observer?.disconnect();
    this.#abort?.abort();
    this.#abort = undefined;
  }

  #definitions(variantId = this.#activeVariant) {
    const definitions = this.#variants?.[variantId]?.variant_options;
    return Array.isArray(definitions) ? definitions.filter((item) => typeof item.optionName === 'string' && typeof item.optionValues === 'string') : [];
  }

  #groups() {
    const groups = this.#variants?.[this.#activeVariant]?.variant_options_v2;
    if (Array.isArray(groups) && groups.length && groups.every(Array.isArray)) return groups;
    return this.#definitions().map((definition) => [definition]);
  }

  #selects() {
    return [...(this.#fields?.querySelectorAll('select[name]') || [])];
  }

  #storageKey() {
    return `eyeone-bundle-choices:${this.dataset.productId}:${this.#activeVariant}`;
  }

  #save = () => {
    if (!this.#definitions().length) return;
    const values = Object.fromEntries(this.#selects().map((select) => [select.name, select.value]));
    try {
      sessionStorage.setItem(this.#storageKey(), JSON.stringify(values));
    } catch {
      // Storage can be unavailable; live form choices remain the source of truth.
    }
  };

  #restore() {
    let values;
    try {
      values = JSON.parse(sessionStorage.getItem(this.#storageKey()) || 'null');
    } catch {
      return;
    }
    if (!values || typeof values !== 'object' || Array.isArray(values)) return;
    for (const select of this.#selects()) {
      const initialOption = [...select.options].find((option) => option.defaultSelected);
      if (initialOption && select.value !== initialOption.value) continue;
      const value = values[select.name];
      if (typeof value !== 'string' || ![...select.options].some((option) => option.value === value && value !== '' && !option.disabled)) continue;
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #sync() {
    if (!this.#selection) return;
    const selects = new Map(this.#selects().map((select) => [select.name, select]));
    const groups = this.#groups();
    const value = groups.map((group) => group.map((definition) => selects.get(`properties[${definition.optionName}]`)?.value || '').join(' ++ ')).join(' <> ');
    if (this.#selection.value !== value) this.#selection.value = value;
    this.#selection.disabled = groups.length === 0;
    this.#save();
  }

  #reconcileVariant = () => {
    if (!this.isConnected || this.#variantInput.value === this.#activeVariant) return;
    this.#save();
    this.#activeVariant = this.#variantInput.value;
    this.dataset.currentVariant = this.#activeVariant;
    const definitions = this.#definitions();
    this.hidden = definitions.length === 0;
    const fragment = document.createDocumentFragment();
    for (const [index, definition] of definitions.entries()) {
      const label = document.createElement('label');
      const select = document.createElement('select');
      select.id = `${this.#form.id}-bundle-option-${index + 1}`;
      select.name = `properties[${definition.optionName}]`;
      select.required = true;
      label.htmlFor = select.id;
      label.textContent = definition.optionName;
      const placeholder = new Option('Please select an option', '');
      placeholder.disabled = true;
      select.append(placeholder);
      const inventories = String(definition.optionInventories ?? '').split(',');
      let selected = false;
      for (const [optionIndex, value] of definition.optionValues.split(', ').entries()) {
        const option = new Option(value, value);
        const inventory = inventories[optionIndex]?.trim();
        if (inventory !== undefined && inventory !== '') {
          option.dataset.inventory = inventory;
          option.disabled = Number(inventory) <= 0;
        }
        if (!selected && !option.disabled) {
          option.defaultSelected = true;
          selected = true;
        }
        select.append(option);
      }
      if (!selected) select.value = '';
      fragment.append(label, select);
    }
    this.#fields.replaceChildren(fragment);
    this.#restore();
    this.#sync();
  };

  #onChange = (event) => {
    if (event.target instanceof HTMLSelectElement && this.#fields.contains(event.target)) this.#sync();
  };

  #onSubmit = () => {
    this.#reconcileVariant();
    this.#sync();
  };

  #onFormData = (event) => {
    this.#reconcileVariant();
    this.#sync();
    if (this.#selection.disabled) return;
    for (const select of this.#selects()) event.formData.set(select.name, select.value);
    event.formData.set(this.#selection.name, this.#selection.value);
  };

  #onReset = () => queueMicrotask(() => {
    if (this.isConnected) this.#sync();
  });

  #onPageShow = () => {
    this.#reconcileVariant();
    this.#sync();
  };
}

if (!customElements.get('eyeone-bundle-options')) customElements.define('eyeone-bundle-options', EyeoneBundleOptions);
